import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { ITabModel } from '@mse-reg/app/components/navigation-tabs';
import { createAppThunk } from '@mse-reg/app/store';
import { ApplicationProfileDto, IApplicationProfileClient, IRoleSelectionClient, RoleDto } from '@mse-reg/clients';
import { authThunks } from '../login/auth.slice';
import { addError } from '../notification.slice';
import { moduleSelectionThunks } from '../selections/module-selection.slice';
import { profileSelectionThunks } from '../selections/profile-selection.slice';
import { getSemesterFilterAsync } from '../selections/semester-selection.slice';

const ACTION_TYPES = {
  GET_PROFILE: 'application/GET_PROFILE',
  ADD_APPLICATION_ERROR: 'application/ADD_APPLICATION_ERROR',
  CLEAR_APPLICATION_ERROR: 'application/CLEAR_APPLICATION_ERROR',
  GET_ROLES: 'application/GET_ROLES',
  REMEMBER_OPEN_TAB: 'application/REMEMBER_TAB',
};

type ApplicationProfile = 'prod' | 'dev' | 'stag' | 'swagger';
const initialState = {
  ribbonEnv: '',
  isProduction: true,
  profiles: [] as ApplicationProfile[],
  isSwaggerEnabled: false,
  examRegistrationEnabled: false,
  loading: true,
  applicationError: '',
  roles: [] as RoleDto[],
  openTabs: {} as { [componentRoute: string]: ITabModel },
};

export type ApplicationState = Readonly<typeof initialState>;

const getAppProfileThunk = createAppThunk(
  ACTION_TYPES.GET_PROFILE,
  async (_, deps: { profileInfoClient: IApplicationProfileClient }) => {
    return await deps.profileInfoClient.getApplicationProfile();
  },
  (r) => ({ profileInfoClient: r.resolve<IApplicationProfileClient>('IApplicationProfileClient') }),
);

const getApplicationRolesThunk = createAppThunk(
  ACTION_TYPES.GET_ROLES,
  async (_, deps: { roleSelectionClient: IRoleSelectionClient }) => {
    return await deps.roleSelectionClient.getRoles();
  },
  (r) => ({ roleSelectionClient: r.resolve<IRoleSelectionClient>('IRoleSelectionClient') }),
);

const loadInitialData = createAppThunk('application/loadInitialData', async (_, deps, dispatch) => {
  try {
    dispatch(appActions.setLoading(true));

    // Profile must always load (also for unauthenticated/dev-login state),
    // otherwise the environment ribbon stays empty.
    const profilePromise = dispatch(getAppProfileThunk()).unwrap();

    try {
      await dispatch(authThunks.getSessionThunk()).unwrap();
      await Promise.all([
        profilePromise,
        dispatch(getSemesterFilterAsync()).unwrap(),
        dispatch(moduleSelectionThunks.fetchModuleFilter()).unwrap(),
        dispatch(getApplicationRolesThunk()).unwrap(),
        dispatch(profileSelectionThunks.fetchLabelsAsync()).unwrap(),
        dispatch(profileSelectionThunks.fetchProfilesAsync()).unwrap(),
      ]);
    } catch {
      // Session-dependent data is optional at app bootstrap in dev mode.
      // Dev login can establish the cookie session afterwards.
      await profilePromise;
    }
  } catch (e) {
    addError(e.message);
  } finally {
    dispatch(appActions.setLoading(false));
  }
});

const appSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    addApplicationError(state, action: PayloadAction<string>) {
      state.applicationError = action.payload;
    },
    clearApplicationError(state) {
      state.applicationError = '';
    },
    rememberOpenTab(state, action: PayloadAction<{ componentRoute: string; selectedTab: ITabModel }>) {
      state.openTabs = { ...state.openTabs, [action.payload.componentRoute]: action.payload.selectedTab };
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getAppProfileThunk.fulfilled, (state, action: PayloadAction<ApplicationProfileDto>) => {
        state.ribbonEnv = action.payload.displayRibbonOnProfiles;
        state.isProduction = action.payload.activeProfiles.includes('prod');
        state.profiles = action.payload.activeProfiles as ApplicationProfile[];
        state.isSwaggerEnabled = action.payload.activeProfiles.includes('swagger');
        state.examRegistrationEnabled = action.payload.examRegistrationEnabled ?? false;
      })
      .addCase(loadInitialData.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadInitialData.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(loadInitialData.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getApplicationRolesThunk.fulfilled, (state, action: PayloadAction<RoleDto[]>) => {
        state.roles = action.payload;
      });
  },
});

export const appReducer = appSlice.reducer;
export const appActions = appSlice.actions;
export const appThunks = { loadInitialData };
